import { ethers } from "ethers";
import { MUCHFI_CHIKYU } from "../config/muchfi";

export const MUCHFI_ROUTER = new ethers.Interface([
  "function getAmountsOut(uint256,address[]) view returns(uint256[])",
  "function swapExactETHForTokens(uint256 amountOutMin,address[] path,address to,uint256 deadline) payable returns(uint256[])",
  "function swapExactTokensForETH(uint256 amountIn,uint256 amountOutMin,address[] path,address to,uint256 deadline) returns(uint256[])",
]);

// Official Base Sepolia V3 deployments. Mainnet activation is a separate release.
export const SEPOLIA_UNISWAP = {
  chainId: 84532,
  router: "0x94cC0AaC535CCDB3C01d6787D6413C739ae12bc4",
  quoter: "0xC5290058841028F1614F3A6F0F5816cAd0df5E27",
  weth: "0x4200000000000000000000000000000000000006",
} as const;
export const UNISWAP_ROUTER = new ethers.Interface([
  "function exactInputSingle((address tokenIn,address tokenOut,uint24 fee,address recipient,uint256 amountIn,uint256 amountOutMinimum,uint160 sqrtPriceLimitX96)) payable returns(uint256)",
  "function unwrapWETH9(uint256 amountMinimum,address recipient) payable",
  "function multicall(uint256 deadline,bytes[] data) payable returns(bytes[])",
]);
export interface PrivateSwapQuote {
  chainId: number;
  pool: string;
  adapter: string;
  inputToken: string;
  outputToken: string;
  amount: string;
  inputAmount: string;
  relayFee: string;
  grossOutput: string;
  protocolFeeBps: number;
  feeTier: number;
  blockNumber: number;
  venue?: "muchfi-v2";
}

export function swapInputAmount(amount: bigint, relayFeeBps: number) {
  if (
    amount <= 0n ||
    amount >= 1n << 120n ||
    !Number.isInteger(relayFeeBps) ||
    relayFeeBps <= 0 ||
    relayFeeBps > 1000
  )
    throw new Error("Invalid private swap amount or relayer fee");
  const relayFee = (amount * BigInt(relayFeeBps)) / 10_000n;
  if (relayFee === 0n || relayFee >= amount)
    throw new Error("Swap amount is too small to cover the relayer fee");
  return { relayFee, inputAmount: amount - relayFee };
}

export async function quotePrivateSwap(params: {
  provider: ethers.Provider;
  chainId: number;
  pool: string;
  inputToken: string;
  outputToken: string;
  amount: bigint;
  relayFeeBps: number;
  /** Explicit fee from the DogeOS live gas quote; never silently increased. */
  relayFee?: bigint;
}): Promise<PrivateSwapQuote> {
  const muchfi = params.chainId === MUCHFI_CHIKYU.chainId;
  if (
    (!muchfi && params.chainId !== SEPOLIA_UNISWAP.chainId) ||
    (await params.provider.getNetwork()).chainId !== BigInt(params.chainId)
  )
    throw new Error("Private swaps are not configured for this network");
  const inputToken = ethers.getAddress(params.inputToken),
    outputToken = ethers.getAddress(params.outputToken);
  if (
    muchfi &&
    (![ethers.ZeroAddress, MUCHFI_CHIKYU.usdc].includes(inputToken) ||
      ![ethers.ZeroAddress, MUCHFI_CHIKYU.usdc].includes(outputToken))
  )
    throw new Error("This MuchFi swap pair is not supported");
  const wrapped = muchfi ? MUCHFI_CHIKYU.wrappedNative : SEPOLIA_UNISWAP.weth;
  const routerAddress = muchfi ? MUCHFI_CHIKYU.router : SEPOLIA_UNISWAP.router;
  const tokenIn = inputToken === ethers.ZeroAddress ? wrapped : inputToken;
  const tokenOut = outputToken === ethers.ZeroAddress ? wrapped : outputToken;
  if (tokenIn === tokenOut) throw new Error("Choose two different swap assets");
  const { inputAmount, relayFee } = muchfi
    ? muchfiInputAmount(params.amount, params.relayFee)
    : swapInputAmount(params.amount, params.relayFeeBps);
  const pool = new ethers.Contract(
    params.pool,
    [
      "function swapAdapter() view returns(address)",
      "function allowedFungibleTokens(address) view returns(bool)",
    ],
    params.provider,
  );
  const adapter = ethers.getAddress(await pool.swapAdapter());
  if (adapter === ethers.ZeroAddress)
    throw new Error("The private swap adapter is not configured");
  const contract = new ethers.Contract(
    adapter,
    [
      "function pool() view returns(address)",
      "function allowedRouters(address) view returns(bool)",
      "function allowedTokens(address) view returns(bool)",
      "function protocolFeeBps() view returns(uint16)",
    ],
    params.provider,
  );
  const [
    adapterPool,
    routerAllowed,
    inputAllowed,
    outputAllowed,
    poolInputAllowed,
    poolOutputAllowed,
    feeBps,
  ] = await Promise.all([
    contract.pool(),
    contract.allowedRouters(routerAddress),
    contract.allowedTokens(inputToken),
    contract.allowedTokens(outputToken),
    pool.allowedFungibleTokens(inputToken),
    pool.allowedFungibleTokens(outputToken),
    contract.protocolFeeBps(),
  ]);
  if (
    ethers.getAddress(adapterPool) !== ethers.getAddress(params.pool) ||
    !routerAllowed ||
    !inputAllowed ||
    !outputAllowed ||
    !poolInputAllowed ||
    !poolOutputAllowed
  )
    throw new Error("This private swap route is not enabled");
  const protocolFeeBps = Number(feeBps);
  if (
    !Number.isInteger(protocolFeeBps) ||
    protocolFeeBps < 0 ||
    protocolFeeBps > 1000
  )
    throw new Error("Invalid swap fee configuration");
  if (muchfi) {
    const amounts = await new ethers.Contract(
      routerAddress,
      MUCHFI_ROUTER,
      params.provider,
    ).getAmountsOut(inputAmount, [tokenIn, tokenOut]);
    if (BigInt(amounts[1]) <= 0n)
      throw new Error("No MuchFi liquidity is available for this amount");
    return {
      chainId: params.chainId,
      pool: ethers.getAddress(params.pool),
      adapter,
      inputToken,
      outputToken,
      amount: params.amount.toString(),
      inputAmount: inputAmount.toString(),
      relayFee: relayFee.toString(),
      // V2 has no selected V3 fee tier; the router quote already includes pool fees.
      grossOutput: amounts[1].toString(),
      protocolFeeBps,
      feeTier: 0,
      blockNumber: await params.provider.getBlockNumber(),
      venue: "muchfi-v2",
    };
  }
  const quoter = new ethers.Contract(
    SEPOLIA_UNISWAP.quoter,
    [
      "function quoteExactInputSingle((address tokenIn,address tokenOut,uint256 amountIn,uint24 fee,uint160 sqrtPriceLimitX96)) returns(uint256,uint160,uint32,uint256)",
    ],
    params.provider,
  );
  // Two bounded calls, on amount changes only. No background quote polling.
  const results = await Promise.allSettled(
    [3000, 500].map(async (fee) => ({
      fee,
      output: BigInt(
        (
          await quoter.quoteExactInputSingle.staticCall([
            tokenIn,
            tokenOut,
            inputAmount,
            fee,
            0n,
          ])
        )[0],
      ),
    })),
  );
  const quotes = results.flatMap((result) =>
    result.status === "fulfilled" && result.value.output > 0n
      ? [result.value]
      : [],
  );
  const best = quotes.sort((a, b) =>
    a.output > b.output ? -1 : a.output < b.output ? 1 : 0,
  )[0];
  if (!best)
    throw new Error("No Uniswap liquidity is available for this amount");
  return {
    chainId: params.chainId,
    pool: ethers.getAddress(params.pool),
    adapter,
    inputToken,
    outputToken,
    amount: params.amount.toString(),
    inputAmount: inputAmount.toString(),
    relayFee: relayFee.toString(),
    grossOutput: best.output.toString(),
    protocolFeeBps,
    feeTier: best.fee,
    blockNumber: await params.provider.getBlockNumber(),
  };
}

export function muchfiInputAmount(amount: bigint, fee?: bigint) {
  if (amount <= 0n || amount >= 1n << 120n || fee === undefined || fee <= 0n)
    throw new Error("Wait for a live MuchFi network fee quote");
  if (fee >= amount)
    throw new Error(
      "The amount does not cover the network fee. Please deposit more funds.",
    );
  return { relayFee: fee, inputAmount: amount - fee };
}

export function buildPrivateSwapRoute(
  quote: PrivateSwapQuote,
  slippageBps: number,
  deadline: bigint,
) {
  const muchfi =
    quote.chainId === MUCHFI_CHIKYU.chainId && quote.venue === "muchfi-v2";
  if (
    !Number.isInteger(quote.protocolFeeBps) ||
    quote.protocolFeeBps < 0 ||
    quote.protocolFeeBps > 1000 ||
    BigInt(quote.inputAmount) <= 0n ||
    BigInt(quote.relayFee) <= 0n ||
    BigInt(quote.inputAmount) + BigInt(quote.relayFee) !==
      BigInt(quote.amount) ||
    ethers.getAddress(quote.adapter) === ethers.ZeroAddress ||
    ethers.getAddress(quote.inputToken) === ethers.getAddress(quote.outputToken)
  )
    throw new Error("Invalid private swap quote");
  if (
    (!muchfi && quote.chainId !== 84532) ||
    !(muchfi ? [0] : [3000, 500]).includes(quote.feeTier) ||
    !Number.isInteger(slippageBps) ||
    slippageBps < 1 ||
    slippageBps > 100 ||
    deadline <= BigInt(Math.floor(Date.now() / 1000))
  )
    throw new Error("Invalid swap route or deadline");
  const grossMinimum =
    (BigInt(quote.grossOutput) * BigInt(10_000 - slippageBps)) / 10_000n;
  const minOutputAmount =
    grossMinimum - (grossMinimum * BigInt(quote.protocolFeeBps)) / 10_000n;
  if (minOutputAmount <= 0n) throw new Error("Swap output is too small");
  if (muchfi) {
    const tokens = [
      ethers.getAddress(quote.inputToken),
      ethers.getAddress(quote.outputToken),
    ];
    if (
      tokens.some((t) => ![ethers.ZeroAddress, MUCHFI_CHIKYU.usdc].includes(t))
    )
      throw new Error("Unsupported MuchFi route");
    const path = tokens.map((t) =>
      t === ethers.ZeroAddress ? MUCHFI_CHIKYU.wrappedNative : t,
    );
    const nativeIn = tokens[0] === ethers.ZeroAddress;
    const swapCallData = nativeIn
      ? MUCHFI_ROUTER.encodeFunctionData("swapExactETHForTokens", [
          grossMinimum,
          path,
          quote.adapter,
          deadline,
        ])
      : MUCHFI_ROUTER.encodeFunctionData("swapExactTokensForETH", [
          BigInt(quote.inputAmount),
          grossMinimum,
          path,
          quote.adapter,
          deadline,
        ]);
    return {
      router: MUCHFI_CHIKYU.router,
      minOutputAmount,
      swapCallData: swapCallData as `0x${string}`,
      deadline,
    };
  }
  const nativeOut = quote.outputToken === ethers.ZeroAddress;
  const exactInput = UNISWAP_ROUTER.encodeFunctionData("exactInputSingle", [
    [
      quote.inputToken === ethers.ZeroAddress
        ? SEPOLIA_UNISWAP.weth
        : quote.inputToken,
      nativeOut ? SEPOLIA_UNISWAP.weth : quote.outputToken,
      quote.feeTier,
      nativeOut ? SEPOLIA_UNISWAP.router : quote.adapter,
      BigInt(quote.inputAmount),
      grossMinimum,
      0n,
    ],
  ]);
  const calls = nativeOut
    ? [
        exactInput,
        UNISWAP_ROUTER.encodeFunctionData("unwrapWETH9", [
          grossMinimum,
          quote.adapter,
        ]),
      ]
    : [exactInput];
  return {
    router: SEPOLIA_UNISWAP.router,
    minOutputAmount,
    swapCallData: UNISWAP_ROUTER.encodeFunctionData("multicall", [
      deadline,
      calls,
    ]) as `0x${string}`,
    deadline,
  };
}
